import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { LoanFormFields } from '../components/LoanFormFields';
import { PartySelector } from '../components/PartySelector';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { formatEuro, formatPercent, formatDate } from '../utils/formatters';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Building2, TrendingUp, AlertTriangle, Send, Check, X, Eye, ExternalLink, HelpCircle, RefreshCw, Users, User, Zap, Leaf } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAG_VIEWER_URL = 'https://bagviewer.kadaster.nl/lvbag/bag-viewer/?zoomlevel=1';

const ENERGY_LABELS = ['A++++', 'A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

// Market standard energy label colors
const ENERGY_LABEL_COLORS = {
  'A++++': '#009036',
  'A+++': '#009036',
  'A++': '#009036',
  'A+': '#52b946',
  'A': '#c8d300',
  'B': '#fff200',
  'C': '#edb731',
  'D': '#d66f1e',
  'E': '#cc232a',
  'F': '#cc232a',
  'G': '#cc232a',
  'N/A': '#64748b'
};

// BAG Lookup Button Component
const BagLookupButton = () => (
  <a 
    href={BAG_VIEWER_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#b4976f] hover:text-[#c8b599] bg-[#264162]/30 hover:bg-[#264162]/50 rounded transition-colors"
    title="Open BAG Viewer om BAG IDs op te zoeken"
  >
    <HelpCircle className="h-3 w-3" />
    BAG?
    <ExternalLink className="h-3 w-3" />
  </a>
);

const assetClasses = [
  { value: 'residential', label: 'residential' },
  { value: 'office', label: 'office' },
  { value: 'retail', label: 'retail' },
  { value: 'light_industrial', label: 'lightIndustrial' },
  { value: 'combination', label: 'combination' },
  { value: 'logistics', label: 'logistics' },
  { value: 'other', label: 'other' }
];

const initialPropertyForm = {
  street: '',
  house_number: '',
  house_number_supplement: '',
  postal_code: '',
  city: '',
  bag_pand_ids: [],
  bag_vbo_ids: [],
  energy_labels: [],
  market_value: '',
  total_sqm_bvo: '',
  total_sqm_vvo: '',
  asset_class: 'residential',
  capex_percentage: '',
  expected_gross_rental: '',
  owners: [],
  tenants: []  // NEW: tenants within property for credit application
};

const initialTenantForm = {
  tenant_name: '',
  bag_vbo_id: '',
  rental_amount: '',
  sqm_rented: '',
  start_date: '',
  end_date: '',
  option_periods: '',
  contract_type: '',
  is_vacancy: false
};

const initialApplicationForm = {
  application_name: '',
  party_id: '',
  properties: [],
  loan_amount_requested: '',
  // Detailed loan structure
  loan_repayment_rate: '',
  loan_rente_type: 'effectief',
  loan_euribor_termijn: '',
  loan_euribor_waarde: '',
  loan_opslag_rente_var: '',
  loan_irs_referentie: '',
  loan_irs_waarde: '',
  loan_opslag_rente_vast: '',
  loan_effectieve_rente: '',
  loan_ltv_max: '',
  loan_icr_min: '',
  loan_dscr_min: '',
  loan_debt_yield_min: '',
  loan_lender_name: '',
  loan_start_date: '',
  loan_expiry_years: '5',
  // Subordinated loan
  has_subordinated_loan: false,
  subordinated_loan_amount: '',
  subordinated_interest_rate: '',
  subordinated_amortization_rate: '',
  capex_overrides: {},
  notes: ''
};

export const CreditApplicationPage = () => {
  const { canEdit, canDelete, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cashflowDialogOpen, setCashflowDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [cashflowData, setCashflowData] = useState([]);
  const [form, setForm] = useState(initialApplicationForm);
  const [propertyForm, setPropertyForm] = useState(initialPropertyForm);
  const [editingPropertyIndex, setEditingPropertyIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // BAG ID input states (same as RealEstatePage)
  const [newBagPandId, setNewBagPandId] = useState('');
  const [newBagVboId, setNewBagVboId] = useState('');
  const [energyLabelInput, setEnergyLabelInput] = useState({ bag_vbo_id: '', energy_label: 'A' });
  const [ownerInput, setOwnerInput] = useState({ party_id: '', ownership_percentage: '' });
  
  // Tenant form state for credit application properties
  const [tenantForm, setTenantForm] = useState(initialTenantForm);
  const [editingTenantIndex, setEditingTenantIndex] = useState(null);
  
  // Energy label auto-fetch state (EP-Online)
  const [fetchingEnergyLabel, setFetchingEnergyLabel] = useState(false);
  const [energyLabelResults, setEnergyLabelResults] = useState([]);
  
  // Euribor rates state
  const [euriborRates, setEuriborRates] = useState(null);
  const [fetchingEuribor, setFetchingEuribor] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch Euribor rates from ECB
  const fetchEuriborRates = async () => {
    setFetchingEuribor(true);
    try {
      const response = await axios.get(`${API}/euribor/rates`, { withCredentials: true });
      if (response.data.success) {
        setEuriborRates(response.data.rates);
        toast.success(response.data.message);
        
        // Auto-fill the euribor value if a term is selected
        if (form.loan_euribor_termijn && response.data.rates[form.loan_euribor_termijn]) {
          setForm(prev => ({
            ...prev,
            loan_euribor_waarde: response.data.rates[form.loan_euribor_termijn].rate.toString()
          }));
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching Euribor rates:', error);
      toast.error('Kon Euribor tarieven niet ophalen');
    } finally {
      setFetchingEuribor(false);
    }
  };

  // Fetch energy labels from EP-Online API
  const fetchEnergyLabels = async () => {
    const bagPandId = propertyForm.bag_pand_ids?.[0];
    const bagVboId = propertyForm.bag_vbo_ids?.[0];
    const postcode = propertyForm.postal_code;
    const huisnummer = propertyForm.house_number;
    
    if (!bagPandId && !bagVboId && !(postcode && huisnummer)) {
      toast.error('Voeg eerst een BAG Pand ID, BAG VBO ID, of postcode+huisnummer toe om energielabels op te halen.');
      return;
    }
    
    setFetchingEnergyLabel(true);
    setEnergyLabelResults([]);
    
    try {
      const params = new URLSearchParams();
      if (bagVboId) {
        params.append('bag_vbo_id', bagVboId);
      } else if (postcode && huisnummer) {
        params.append('postcode', postcode);
        params.append('huisnummer', huisnummer);
        if (propertyForm.house_number_supplement) {
          const supplement = propertyForm.house_number_supplement.trim();
          if (/^[A-Za-z]$/.test(supplement)) {
            params.append('huisletter', supplement.toUpperCase());
          } else {
            params.append('huisnummertoevoeging', supplement);
          }
        }
        if (bagPandId) {
          params.append('bag_pand_id', bagPandId);
        }
      } else {
        toast.error('Voeg eerst een BAG VBO ID of postcode+huisnummer toe.');
        setFetchingEnergyLabel(false);
        return;
      }
      
      const response = await axios.get(`${API}/energylabels/fetch?${params.toString()}`, { withCredentials: true });
      
      if (response.data.success && response.data.labels?.length > 0) {
        setEnergyLabelResults(response.data.labels);
        toast.success(response.data.message);
        
        if (response.data.labels.length === 1) {
          applyEnergyLabel(response.data.labels[0]);
        }
      } else {
        toast.info(response.data.message || 'Geen energielabels gevonden.');
      }
    } catch (error) {
      console.error('Error fetching energy labels:', error);
      toast.error('Fout bij ophalen energielabels. Controleer of EP_ONLINE_API_KEY is geconfigureerd.');
    } finally {
      setFetchingEnergyLabel(false);
    }
  };

  // Apply selected energy label to property form
  const applyEnergyLabel = (labelData) => {
    const newLabel = {
      bag_vbo_id: labelData.bag_vbo_id || propertyForm.bag_vbo_ids?.[0] || 'auto',
      energy_label: labelData.energielabel_klasse
    };
    
    const existingIndex = propertyForm.energy_labels.findIndex(
      el => el.bag_vbo_id === newLabel.bag_vbo_id
    );
    
    if (existingIndex >= 0) {
      const updatedLabels = [...propertyForm.energy_labels];
      updatedLabels[existingIndex] = newLabel;
      setPropertyForm({ ...propertyForm, energy_labels: updatedLabels });
    } else {
      setPropertyForm({
        ...propertyForm,
        energy_labels: [...propertyForm.energy_labels, newLabel]
      });
    }
    
    toast.success(`Energielabel ${labelData.energielabel_klasse} toegepast`);
    setEnergyLabelResults([]);
  };

  // Auto-fill Euribor when term changes
  const handleEuriborTermChange = (term) => {
    setForm(prev => ({ ...prev, loan_euribor_termijn: term }));
    if (euriborRates && euriborRates[term]) {
      setForm(prev => ({
        ...prev,
        loan_euribor_termijn: term,
        loan_euribor_waarde: euriborRates[term].rate.toString()
      }));
    }
  };

  // Handler for when a new party is created inline
  const handlePartyCreated = (newParty) => {
    setParties(prev => [...prev, newParty]);
  };

  const fetchData = async () => {
    try {
      const [appsRes, partiesRes] = await Promise.all([
        axios.get(`${API}/credit-applications`, { withCredentials: true }),
        axios.get(`${API}/parties`, { withCredentials: true })
      ]);
      setApplications(appsRes.data);
      setParties(partiesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (app = null) => {
    if (app) {
      setSelectedApplication(app);
      setForm({
        application_name: app.application_name,
        party_id: app.legal_owner_id || app.party_id || '',
        properties: app.properties || [],
        loan_amount_requested: app.loan_amount_requested,
        // Detailed loan structure
        loan_repayment_rate: app.loan_repayment_rate || '',
        loan_rente_type: app.loan_rente_type || 'effectief',
        loan_euribor_termijn: app.loan_euribor_termijn || '',
        loan_euribor_waarde: app.loan_euribor_waarde || '',
        loan_opslag_rente_var: app.loan_opslag_rente_var || '',
        loan_irs_referentie: app.loan_irs_referentie || '',
        loan_irs_waarde: app.loan_irs_waarde || '',
        loan_opslag_rente_vast: app.loan_opslag_rente_vast || '',
        loan_effectieve_rente: app.loan_effectieve_rente || '',
        loan_ltv_max: app.loan_ltv_max || '',
        loan_icr_min: app.loan_icr_min || '',
        loan_dscr_min: app.loan_dscr_min || '',
        loan_debt_yield_min: app.loan_debt_yield_min || '',
        loan_lender_name: app.loan_lender_name || '',
        loan_start_date: app.loan_start_date || '',
        loan_expiry_years: app.loan_expiry_years || '5',
        // Subordinated
        has_subordinated_loan: app.has_subordinated_loan || false,
        subordinated_loan_amount: app.subordinated_loan_amount || '',
        subordinated_interest_rate: app.subordinated_interest_rate || '',
        subordinated_amortization_rate: app.subordinated_amortization_rate || '',
        capex_overrides: app.capex_overrides || {},
        notes: app.notes || ''
      });
    } else {
      setSelectedApplication(null);
      setForm(initialApplicationForm);
    }
    setDialogOpen(true);
  };

  const handleOpenPropertyDialog = (index = null) => {
    if (index !== null) {
      setEditingPropertyIndex(index);
      const prop = form.properties[index];
      setPropertyForm({
        ...prop,
        bag_pand_ids: prop.bag_pand_ids || [],
        bag_vbo_ids: prop.bag_vbo_ids || [],
        energy_labels: prop.energy_labels || [],
        owners: prop.owners || prop.legal_owners || []
      });
    } else {
      setEditingPropertyIndex(null);
      setPropertyForm(initialPropertyForm);
    }
    setNewBagPandId('');
    setNewBagVboId('');
    setPropertyDialogOpen(true);
  };

  // BAG ID handlers (same as RealEstatePage)
  const validateBagId = (id, type) => {
    const cleanId = id.trim();
    if (!/^\d{16}$/.test(cleanId)) {
      toast.error(`Invalid BAG ${type} ID format. Must be 16 digits.`);
      return false;
    }
    return true;
  };

  const handleAddBagPandId = () => {
    if (!newBagPandId.trim()) return;
    if (!validateBagId(newBagPandId, 'Pand')) return;
    if (propertyForm.bag_pand_ids.includes(newBagPandId.trim())) {
      toast.error('This BAG Pand ID already exists');
      return;
    }
    setPropertyForm({
      ...propertyForm,
      bag_pand_ids: [...propertyForm.bag_pand_ids, newBagPandId.trim()]
    });
    setNewBagPandId('');
  };

  const handleRemoveBagPandId = (id) => {
    setPropertyForm({
      ...propertyForm,
      bag_pand_ids: propertyForm.bag_pand_ids.filter(pid => pid !== id)
    });
  };

  const handleAddBagVboId = () => {
    if (!newBagVboId.trim()) return;
    if (!validateBagId(newBagVboId, 'VBO')) return;
    if (propertyForm.bag_vbo_ids.includes(newBagVboId.trim())) {
      toast.error('This BAG VBO ID already exists');
      return;
    }
    setPropertyForm({
      ...propertyForm,
      bag_vbo_ids: [...propertyForm.bag_vbo_ids, newBagVboId.trim()]
    });
    setNewBagVboId('');
  };

  const handleRemoveBagVboId = (id) => {
    setPropertyForm({
      ...propertyForm,
      bag_vbo_ids: propertyForm.bag_vbo_ids.filter(vid => vid !== id),
      energy_labels: propertyForm.energy_labels.filter(el => el.bag_vbo_id !== id)
    });
  };

  const handleAddEnergyLabel = () => {
    if (!energyLabelInput.bag_vbo_id) return;
    const exists = propertyForm.energy_labels.some(el => el.bag_vbo_id === energyLabelInput.bag_vbo_id);
    if (exists) {
      setPropertyForm({
        ...propertyForm,
        energy_labels: propertyForm.energy_labels.map(el => 
          el.bag_vbo_id === energyLabelInput.bag_vbo_id ? { ...energyLabelInput } : el
        )
      });
    } else {
      setPropertyForm({
        ...propertyForm,
        energy_labels: [...propertyForm.energy_labels, { ...energyLabelInput }]
      });
    }
    setEnergyLabelInput({ bag_vbo_id: '', energy_label: 'A' });
  };

  const handleRemoveEnergyLabel = (vboId) => {
    setPropertyForm({
      ...propertyForm,
      energy_labels: propertyForm.energy_labels.filter(el => el.bag_vbo_id !== vboId)
    });
  };

  const handleAddOwner = () => {
    if (!ownerInput.party_id || !ownerInput.ownership_percentage) return;
    const exists = propertyForm.owners.some(o => o.party_id === ownerInput.party_id);
    if (exists) {
      setPropertyForm({
        ...propertyForm,
        owners: propertyForm.owners.map(o => 
          o.party_id === ownerInput.party_id ? { ...ownerInput, ownership_percentage: parseFloat(ownerInput.ownership_percentage) } : o
        )
      });
    } else {
      setPropertyForm({
        ...propertyForm,
        owners: [...propertyForm.owners, { ...ownerInput, ownership_percentage: parseFloat(ownerInput.ownership_percentage) }]
      });
    }
    setOwnerInput({ party_id: '', ownership_percentage: '' });
  };

  const handleRemoveOwner = (partyId) => {
    setPropertyForm({
      ...propertyForm,
      owners: propertyForm.owners.filter(o => o.party_id !== partyId)
    });
  };

  // Tenant handlers for credit application properties
  const handleAddTenant = () => {
    if (!tenantForm.tenant_name || !tenantForm.rental_amount || !tenantForm.sqm_rented) {
      toast.error('Vul huurder naam, huursom en m² in');
      return;
    }
    const newTenant = {
      ...tenantForm,
      rental_amount: parseFloat(tenantForm.rental_amount) || 0,
      sqm_rented: parseFloat(tenantForm.sqm_rented) || 0
    };
    
    if (editingTenantIndex !== null) {
      const updatedTenants = [...propertyForm.tenants];
      updatedTenants[editingTenantIndex] = newTenant;
      setPropertyForm({ ...propertyForm, tenants: updatedTenants });
      setEditingTenantIndex(null);
    } else {
      setPropertyForm({ ...propertyForm, tenants: [...propertyForm.tenants, newTenant] });
    }
    setTenantForm(initialTenantForm);
  };

  const handleEditTenant = (index) => {
    const tenant = propertyForm.tenants[index];
    setTenantForm({
      ...tenant,
      rental_amount: String(tenant.rental_amount),
      sqm_rented: String(tenant.sqm_rented)
    });
    setEditingTenantIndex(index);
  };

  const handleRemoveTenant = (index) => {
    setPropertyForm({
      ...propertyForm,
      tenants: propertyForm.tenants.filter((_, i) => i !== index)
    });
    if (editingTenantIndex === index) {
      setTenantForm(initialTenantForm);
      setEditingTenantIndex(null);
    }
  };

  const getPartyName = (partyId) => {
    const party = parties.find(p => p.party_id === partyId);
    return party?.name || partyId;
  };

  const handleSaveProperty = () => {
    // Validation
    if (!propertyForm.street || !propertyForm.house_number || !propertyForm.postal_code || !propertyForm.city) {
      toast.error('Vul alle verplichte adresvelden in');
      return;
    }
    if (!propertyForm.market_value) {
      toast.error('Marktwaarde is verplicht');
      return;
    }

    const newProp = {
      ...propertyForm,
      market_value: parseFloat(propertyForm.market_value) || 0,
      total_sqm_bvo: parseFloat(propertyForm.total_sqm_bvo) || 0,
      total_sqm_vvo: parseFloat(propertyForm.total_sqm_vvo) || 0,
      capex_percentage: parseFloat(propertyForm.capex_percentage) || 0,
      expected_gross_rental: parseFloat(propertyForm.expected_gross_rental) || 0
    };

    if (editingPropertyIndex !== null) {
      const updatedProperties = [...form.properties];
      updatedProperties[editingPropertyIndex] = newProp;
      setForm({ ...form, properties: updatedProperties });
    } else {
      setForm({ ...form, properties: [...form.properties, newProp] });
    }
    setPropertyDialogOpen(false);
    toast.success('Vastgoed toegevoegd');
  };

  const handleRemoveProperty = (index) => {
    const updatedProperties = form.properties.filter((_, i) => i !== index);
    setForm({ ...form, properties: updatedProperties });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.application_name) {
      toast.error('Naam aanvraag is verplicht');
      return;
    }
    if (!form.party_id) {
      toast.error('Selecteer een partij');
      return;
    }
    if (!form.loan_amount_requested || parseFloat(form.loan_amount_requested) <= 0) {
      toast.error('Vul een geldig leningbedrag in');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        application_name: form.application_name,
        legal_owner_id: form.party_id,
        properties: form.properties,
        loan_amount_requested: parseFloat(form.loan_amount_requested) || 0,
        // Detailed loan structure
        loan_repayment_rate: parseFloat(form.loan_repayment_rate) || 2.0,
        loan_rente_type: form.loan_rente_type || 'effectief',
        loan_euribor_termijn: form.loan_euribor_termijn || null,
        loan_euribor_waarde: form.loan_euribor_waarde ? parseFloat(form.loan_euribor_waarde) : null,
        loan_opslag_rente_var: form.loan_opslag_rente_var ? parseFloat(form.loan_opslag_rente_var) : null,
        loan_irs_referentie: form.loan_irs_referentie || null,
        loan_irs_waarde: form.loan_irs_waarde ? parseFloat(form.loan_irs_waarde) : null,
        loan_opslag_rente_vast: form.loan_opslag_rente_vast ? parseFloat(form.loan_opslag_rente_vast) : null,
        loan_effectieve_rente: form.loan_effectieve_rente ? parseFloat(form.loan_effectieve_rente) : null,
        loan_ltv_max: form.loan_ltv_max ? parseFloat(form.loan_ltv_max) : null,
        loan_icr_min: form.loan_icr_min ? parseFloat(form.loan_icr_min) : null,
        loan_dscr_min: form.loan_dscr_min ? parseFloat(form.loan_dscr_min) : null,
        loan_debt_yield_min: form.loan_debt_yield_min ? parseFloat(form.loan_debt_yield_min) : null,
        loan_lender_name: form.loan_lender_name || 'TBD',
        loan_start_date: form.loan_start_date || null,
        loan_expiry_years: parseInt(form.loan_expiry_years) || 5,
        // Subordinated loan
        has_subordinated_loan: form.has_subordinated_loan,
        subordinated_loan_amount: form.has_subordinated_loan ? parseFloat(form.subordinated_loan_amount) || 0 : 0,
        subordinated_interest_rate: form.has_subordinated_loan ? parseFloat(form.subordinated_interest_rate) || 0 : 0,
        subordinated_amortization_rate: form.has_subordinated_loan ? parseFloat(form.subordinated_amortization_rate) || 0 : 0,
        capex_overrides: form.capex_overrides,
        notes: form.notes
      };

      if (selectedApplication) {
        await axios.put(`${API}/credit-applications/${selectedApplication.application_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/credit-applications`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await axios.put(`${API}/credit-applications/${appId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Status gewijzigd naar ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleViewCashflow = async (app) => {
    try {
      const response = await axios.get(`${API}/credit-applications/${app.application_id}/cashflow`, { withCredentials: true });
      setCashflowData(response.data);
      setSelectedApplication(app);
      setCashflowDialogOpen(true);
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/credit-applications/${selectedApplication.application_id}`, { withCredentials: true });
      toast.success(t('deletedSuccessfully'));
      setDeleteDialogOpen(false);
      setSelectedApplication(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-500/20 text-slate-400',
      submitted: 'bg-blue-500/20 text-blue-400',
      under_review: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-emerald-500/20 text-emerald-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      draft: t('draft'),
      submitted: t('submitted'),
      under_review: t('underReview'),
      approved: t('approved'),
      rejected: t('rejected')
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const calculateEquity = () => {
    const totalMarketValue = form.properties.reduce((sum, p) => sum + (parseFloat(p.market_value) || 0), 0);
    const loanAmount = parseFloat(form.loan_amount_requested) || 0;
    return totalMarketValue - loanAmount;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-96 bg-slate-800" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="credit-application-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Comfortaa']">{t('creditApplication')}</h1>
            <p className="text-slate-400 mt-1">
              {applications.length} aanvra{applications.length === 1 ? 'ag' : 'gen'}
            </p>
          </div>
          <Button 
            className="bg-[#b4976f] hover:bg-[#a38663]"
            onClick={() => handleOpenDialog()}
            data-testid="new-application-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('newCreditApplication')}
          </Button>
        </div>

        {/* Applications Table */}
        <Card className="bg-[#1e293b] border-slate-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t('applicationName')}</TableHead>
                  <TableHead className="text-slate-400">{t('parties')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('loanAmountRequested')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('equityAmount')}</TableHead>
                  <TableHead className="text-slate-400">{t('status')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <TableRow key={app.application_id} className="border-slate-700 table-row-hover">
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-[#b4976f]" />
                          {app.application_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {getPartyName(app.party_id || app.legal_owner_id)}
                      </TableCell>
                      <TableCell className="text-right text-[#c8b599] euro-amount">
                        {formatEuro(app.loan_amount_requested)}
                      </TableCell>
                      <TableCell className="text-right text-white euro-amount">
                        {formatEuro(app.equity_amount)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(app.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewCashflow(app)}
                            title={t('viewCashflow')}
                          >
                            <TrendingUp className="h-4 w-4 text-slate-400 hover:text-white" />
                          </Button>
                          {app.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(app.application_id, 'submitted')}
                              title={t('submitApplication')}
                            >
                              <Send className="h-4 w-4 text-blue-400 hover:text-blue-300" />
                            </Button>
                          )}
                          {isAdmin() && app.status === 'submitted' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(app.application_id, 'under_review')}
                              title="Start Review"
                            >
                              <Eye className="h-4 w-4 text-amber-400 hover:text-amber-300" />
                            </Button>
                          )}
                          {isAdmin() && app.status === 'under_review' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(app.application_id, 'approved')}
                                title={t('approveApplication')}
                              >
                                <Check className="h-4 w-4 text-emerald-400 hover:text-emerald-300" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(app.application_id, 'rejected')}
                                title={t('rejectApplication')}
                              >
                                <X className="h-4 w-4 text-red-400 hover:text-red-300" />
                              </Button>
                            </>
                          )}
                          {(app.status === 'draft' || app.status === 'rejected') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(app)}
                            >
                              <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                            </Button>
                          )}
                          {app.status !== 'approved' && canDelete() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedApplication(app);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                      <p>{t('noData')}</p>
                      <Button 
                        variant="link" 
                        className="text-[#b4976f] mt-2"
                        onClick={() => handleOpenDialog()}
                      >
                        {t('newCreditApplication')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Application Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {selectedApplication ? t('editCreditApplication') : t('newCreditApplication')}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Vul de gegevens in voor de kredietaanvraag
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('applicationName')}</Label>
                  <Input
                    value={form.application_name}
                    onChange={(e) => setForm({ ...form, application_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="app-name-input"
                  />
                </div>
                {/* Borrower Selection with inline creation */}
                <PartySelector
                  parties={parties}
                  selectedIds={form.party_id ? [form.party_id] : []}
                  onSelectionChange={(ids) => setForm({ ...form, party_id: ids[0] || '' })}
                  onPartyCreated={handlePartyCreated}
                  label={`${t('parties')} (Kredietnemer)`}
                  placeholder={t('selectOwner')}
                  colorScheme="emerald"
                  icon={<Users className="h-4 w-4 text-[#b4976f]" />}
                />
              </div>

              {/* Loan Details - Basic */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('loanAmountRequested')} (€)</Label>
                  <Input
                    type="number"
                    value={form.loan_amount_requested}
                    onChange={(e) => setForm({ ...form, loan_amount_requested: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    min="0"
                    step="0.01"
                    data-testid="loan-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('equityAmount')} (€)</Label>
                  <Input
                    value={formatEuro(calculateEquity())}
                    className="bg-slate-800 border-slate-700 text-[#b4976f]"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Aflossingspercentage (%)</Label>
                  <Input
                    type="number"
                    value={form.loan_repayment_rate}
                    onChange={(e) => setForm({ ...form, loan_repayment_rate: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="bijv. 2.0"
                    data-testid="credit-repayment-rate"
                  />
                </div>
              </div>

              {/* Detailed Loan Structure - Using Reusable Component */}
              <LoanFormFields
                form={form}
                setForm={setForm}
                fieldPrefix="loan_"
                showProResto={false}
                showLenderDetails={true}
                compact={true}
                onFetchEuribor={fetchEuriborRates}
                onEuriborTermChange={handleEuriborTermChange}
                euriborRates={euriborRates}
                fetchingEuribor={fetchingEuribor}
              />

              {/* Subordinated Loan Toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.has_subordinated_loan}
                  onCheckedChange={(checked) => setForm({ ...form, has_subordinated_loan: checked })}
                  data-testid="subordinated-switch"
                />
                <Label className="text-slate-300">{t('hasSubordinatedLoan')}</Label>
              </div>

              {/* Subordinated Loan Details */}
              {form.has_subordinated_loan && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('subordinatedAmount')} (€)</Label>
                    <Input
                      type="number"
                      value={form.subordinated_loan_amount}
                      onChange={(e) => setForm({ ...form, subordinated_loan_amount: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      step="0.01"
                      data-testid="sub-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('subordinatedInterestRate')} (%)</Label>
                    <Input
                      type="number"
                      value={form.subordinated_interest_rate}
                      onChange={(e) => setForm({ ...form, subordinated_interest_rate: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      max="100"
                      step="0.01"
                      data-testid="sub-interest-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('subordinatedAmortization')} (%)</Label>
                    <Input
                      type="number"
                      value={form.subordinated_amortization_rate}
                      onChange={(e) => setForm({ ...form, subordinated_amortization_rate: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      min="0"
                      max="100"
                      step="0.01"
                      data-testid="sub-amort-input"
                    />
                  </div>
                </div>
              )}

              {/* Properties Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 text-lg">{t('realEstate')}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenPropertyDialog()}
                    className="border-[#b4976f] text-[#b4976f]"
                    data-testid="add-property-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('addPropertyToApplication')}
                  </Button>
                </div>
                
                {form.properties.length > 0 ? (
                  <div className="space-y-2">
                    {form.properties.map((prop, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-[#b4976f]" />
                          <div>
                            <p className="text-white font-medium">
                              {prop.street} {prop.house_number}
                            </p>
                            <p className="text-sm text-slate-400">
                              {prop.city} | {formatEuro(prop.market_value)} | GRI: {formatEuro(prop.expected_gross_rental)}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenPropertyDialog(index)}
                          >
                            <Edit className="h-4 w-4 text-slate-400" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveProperty(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">
                    Nog geen vastgoed toegevoegd
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-300">{t('notes')}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-slate-600 text-slate-300"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#b4976f] hover:bg-[#a38663]"
                  disabled={saving || !form.party_id || form.properties.length === 0}
                  data-testid="save-application-btn"
                >
                  {saving ? t('loading') : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Property Dialog */}
        <Dialog open={propertyDialogOpen} onOpenChange={setPropertyDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {editingPropertyIndex !== null ? t('editProperty') : t('addProperty')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Basic Address Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('street')}</Label>
                  <Input
                    value={propertyForm.street}
                    onChange={(e) => setPropertyForm({ ...propertyForm, street: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="credit-property-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('houseNumber')}</Label>
                    <Input
                      value={propertyForm.house_number}
                      onChange={(e) => setPropertyForm({ ...propertyForm, house_number: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                      data-testid="credit-property-house-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Toev.</Label>
                    <Input
                      value={propertyForm.house_number_supplement}
                      onChange={(e) => setPropertyForm({ ...propertyForm, house_number_supplement: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('postalCode')}</Label>
                  <Input
                    value={propertyForm.postal_code}
                    onChange={(e) => setPropertyForm({ ...propertyForm, postal_code: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="credit-property-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('city')}</Label>
                  <Input
                    value={propertyForm.city}
                    onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    data-testid="credit-property-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('assetClass')}</Label>
                  <Select 
                    value={propertyForm.asset_class} 
                    onValueChange={(value) => setPropertyForm({ ...propertyForm, asset_class: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {assetClasses.map((ac) => (
                        <SelectItem key={ac.value} value={ac.value} className="text-white hover:bg-slate-800">
                          {t(ac.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('marketValue')} (€)</Label>
                  <Input
                    type="number"
                    value={propertyForm.market_value}
                    onChange={(e) => setPropertyForm({ ...propertyForm, market_value: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    required
                    min="0"
                    data-testid="credit-property-market-value"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('expectedGrossRental')} (€)</Label>
                  <Input
                    type="number"
                    value={propertyForm.expected_gross_rental}
                    onChange={(e) => setPropertyForm({ ...propertyForm, expected_gross_rental: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    min="0"
                    data-testid="credit-property-gri"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('totalSqmBvo')}</Label>
                  <Input
                    type="number"
                    value={propertyForm.total_sqm_bvo}
                    onChange={(e) => setPropertyForm({ ...propertyForm, total_sqm_bvo: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('totalSqmVvo')}</Label>
                  <Input
                    type="number"
                    value={propertyForm.total_sqm_vvo}
                    onChange={(e) => setPropertyForm({ ...propertyForm, total_sqm_vvo: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('capexPercentage')} (%)</Label>
                  <Input
                    type="number"
                    value={propertyForm.capex_percentage}
                    onChange={(e) => setPropertyForm({ ...propertyForm, capex_percentage: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              {/* BAG Pand IDs Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">{t('bagPandId')}</Label>
                  <BagLookupButton />
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newBagPandId} 
                    onChange={(e) => setNewBagPandId(e.target.value)} 
                    className="bg-slate-900 border-slate-700 text-white flex-1" 
                    placeholder="Enter BAG Pand ID (16 digits)"
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBagPandId(); } }}
                    data-testid="credit-bag-pand-input"
                  />
                  <Button type="button" onClick={handleAddBagPandId} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.bag_pand_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.bag_pand_ids.map((id, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm text-white">
                        {id}
                        <button type="button" onClick={() => handleRemoveBagPandId(id)} className="text-red-400 hover:text-red-300 ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* BAG VBO IDs Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">{t('bagVboId')}</Label>
                  <BagLookupButton />
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newBagVboId} 
                    onChange={(e) => setNewBagVboId(e.target.value)} 
                    className="bg-slate-900 border-slate-700 text-white flex-1" 
                    placeholder="Enter BAG VBO ID (16 digits)"
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBagVboId(); } }}
                    data-testid="credit-bag-vbo-input"
                  />
                  <Button type="button" onClick={handleAddBagVboId} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.bag_vbo_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.bag_vbo_ids.map((id, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm text-white">
                        {id}
                        <button type="button" onClick={() => handleRemoveBagVboId(id)} className="text-red-400 hover:text-red-300 ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Energy Labels Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Leaf className="h-4 w-4 text-emerald-400" />
                  {t('energyLabel')} per BAG VBO ID
                </Label>
                
                {/* Auto-fetch energy label button */}
                <div className="flex items-center gap-2 mb-3">
                  <Button
                    type="button"
                    onClick={fetchEnergyLabels}
                    disabled={fetchingEnergyLabel}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid="credit-fetch-energy-label-btn"
                  >
                    {fetchingEnergyLabel ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    {fetchingEnergyLabel ? 'Ophalen...' : 'Energielabel Ophalen (EP-Online)'}
                  </Button>
                  <span className="text-xs text-slate-500">
                    Zoekt op: BAG VBO ID → Postcode+Huisnummer
                  </span>
                </div>

                {/* Energy label results from API */}
                {energyLabelResults.length > 0 && (
                  <div className="p-3 rounded bg-emerald-900/30 border border-emerald-700 mb-3">
                    <p className="text-sm text-emerald-300 mb-2">Gevonden energielabels - klik om toe te passen:</p>
                    <div className="flex flex-wrap gap-2">
                      {energyLabelResults.map((label, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => applyEnergyLabel(label)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-sm transition-colors"
                        >
                          <span style={{ color: ENERGY_LABEL_COLORS[label.energielabel_klasse] || '#64748b', fontWeight: 'bold' }}>
                            {label.energielabel_klasse}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="text-slate-300">{label.adres || label.bag_vbo_id}</span>
                          {label.energielabel_registratiedatum && (
                            <span className="text-slate-500 text-xs">({label.energielabel_registratiedatum})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual energy label entry */}
                {propertyForm.bag_vbo_ids.length === 0 ? (
                  <p className="text-sm text-slate-500">Add BAG VBO IDs above first to assign energy labels</p>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Select value={energyLabelInput.bag_vbo_id} onValueChange={(value) => setEnergyLabelInput({ ...energyLabelInput, bag_vbo_id: value })}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                          <SelectValue placeholder="Select BAG VBO ID" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {propertyForm.bag_vbo_ids.map((vboId) => (
                            <SelectItem key={vboId} value={vboId} className="text-white hover:bg-slate-800">
                              {vboId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={energyLabelInput.energy_label} onValueChange={(value) => setEnergyLabelInput({ ...energyLabelInput, energy_label: value })}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {ENERGY_LABELS.map((label) => (
                            <SelectItem key={label} value={label} className="text-white hover:bg-slate-800">
                              <span style={{ color: ENERGY_LABEL_COLORS[label] }}>{label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" onClick={handleAddEnergyLabel} className="bg-[#264162]" disabled={!energyLabelInput.bag_vbo_id}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {propertyForm.energy_labels.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {propertyForm.energy_labels.map((el, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm">
                            {el.bag_vbo_id}: <span style={{ color: ENERGY_LABEL_COLORS[el.energy_label] || '#b4976f' }}>{el.energy_label}</span>
                            <button type="button" onClick={() => handleRemoveEnergyLabel(el.bag_vbo_id)} className="text-red-400 hover:text-red-300">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Owners Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <Label className="text-slate-300">{t('parties')} (Eigenaren)</Label>
                <div className="flex gap-2">
                  <Select value={ownerInput.party_id} onValueChange={(value) => setOwnerInput({ ...ownerInput, party_id: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                      <SelectValue placeholder={t('selectOwner')} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {parties.map((party) => (
                        <SelectItem key={party.party_id} value={party.party_id} className="text-white hover:bg-slate-800">
                          {party.name} ({party.party_type === 'bv' ? 'B.V.' : 'Particulier'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    type="number" 
                    value={ownerInput.ownership_percentage} 
                    onChange={(e) => setOwnerInput({ ...ownerInput, ownership_percentage: e.target.value })} 
                    className="bg-slate-900 border-slate-700 text-white w-24" 
                    placeholder="%" 
                    min="0" 
                    max="100" 
                  />
                  <Button type="button" onClick={handleAddOwner} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.owners.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.owners.map((lo, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm">
                        {getPartyName(lo.party_id)}: <span className="text-[#b4976f]">{lo.ownership_percentage}%</span>
                        <button type="button" onClick={() => handleRemoveOwner(lo.party_id)} className="text-red-400 hover:text-red-300">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tenants Section */}
              <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 font-medium">{t('tenants')} (Huurders)</Label>
                  <span className="text-xs text-slate-500">{propertyForm.tenants.length} huurder(s)</span>
                </div>
                
                {/* Tenant Input Form */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input
                    value={tenantForm.tenant_name}
                    onChange={(e) => setTenantForm({ ...tenantForm, tenant_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white col-span-2"
                    placeholder="Huurder naam *"
                    data-testid="credit-tenant-name"
                  />
                  {propertyForm.bag_vbo_ids.length > 0 ? (
                    <Select value={tenantForm.bag_vbo_id} onValueChange={(value) => setTenantForm({ ...tenantForm, bag_vbo_id: value })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder="BAG VBO ID" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {propertyForm.bag_vbo_ids.map((vboId) => (
                          <SelectItem key={vboId} value={vboId} className="text-white hover:bg-slate-800">{vboId}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={tenantForm.bag_vbo_id}
                      onChange={(e) => setTenantForm({ ...tenantForm, bag_vbo_id: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder="BAG VBO ID"
                    />
                  )}
                  <Select value={tenantForm.contract_type} onValueChange={(value) => setTenantForm({ ...tenantForm, contract_type: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue placeholder="Contract type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="ROZ Kantoorruimte" className="text-white hover:bg-slate-800">ROZ Kantoorruimte</SelectItem>
                      <SelectItem value="ROZ Winkelruimte" className="text-white hover:bg-slate-800">ROZ Winkelruimte</SelectItem>
                      <SelectItem value="Other" className="text-white hover:bg-slate-800">Anders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input
                    type="number"
                    value={tenantForm.rental_amount}
                    onChange={(e) => setTenantForm({ ...tenantForm, rental_amount: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="Huursom/jaar € *"
                    min="0"
                  />
                  <Input
                    type="number"
                    value={tenantForm.sqm_rented}
                    onChange={(e) => setTenantForm({ ...tenantForm, sqm_rented: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="m² gehuurd *"
                    min="0"
                  />
                  <Input
                    type="date"
                    value={tenantForm.start_date}
                    onChange={(e) => setTenantForm({ ...tenantForm, start_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="Start"
                  />
                  <Input
                    type="date"
                    value={tenantForm.end_date}
                    onChange={(e) => setTenantForm({ ...tenantForm, end_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="Einde"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    value={tenantForm.option_periods}
                    onChange={(e) => setTenantForm({ ...tenantForm, option_periods: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white flex-1"
                    placeholder="Optietermijnen (bijv. 2x5 jaar)"
                  />
                  <label className="flex items-center gap-2 text-sm text-slate-400">
                    <input
                      type="checkbox"
                      checked={tenantForm.is_vacancy}
                      onChange={(e) => setTenantForm({ ...tenantForm, is_vacancy: e.target.checked })}
                      className="rounded border-slate-700 bg-slate-900"
                    />
                    Leegstand
                  </label>
                  <Button type="button" onClick={handleAddTenant} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4 mr-1" />
                    {editingTenantIndex !== null ? 'Wijzig' : 'Voeg toe'}
                  </Button>
                </div>

                {/* Added Tenants List */}
                {propertyForm.tenants.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {propertyForm.tenants.map((tenant, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-700/50 text-sm">
                        <div className="flex-1">
                          <span className={tenant.is_vacancy ? 'text-amber-400' : 'text-white'}>
                            {tenant.is_vacancy ? '⚠ Leegstand' : tenant.tenant_name}
                          </span>
                          <span className="text-slate-400 ml-2">
                            €{tenant.rental_amount?.toLocaleString()}/jr • {tenant.sqm_rented}m²
                          </span>
                          {tenant.contract_type && (
                            <span className="text-[#b4976f] ml-2 text-xs">{tenant.contract_type}</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => handleEditTenant(idx)} className="text-slate-400 hover:text-white p-1">
                            <Edit className="h-3 w-3" />
                          </button>
                          <button type="button" onClick={() => handleRemoveTenant(idx)} className="text-red-400 hover:text-red-300 p-1">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPropertyDialogOpen(false)}
                  className="border-slate-600 text-slate-300"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveProperty}
                  className="bg-[#b4976f] hover:bg-[#a38663]"
                  data-testid="credit-save-property-btn"
                >
                  {t('save')}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cashflow Dialog */}
        <Dialog open={cashflowDialogOpen} onOpenChange={setCashflowDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-6xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {t('cashflowOverview')} - {selectedApplication?.application_name}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400 sticky left-0 bg-[#1e293b]">{t('year')}</TableHead>
                    {cashflowData.map((row) => (
                      <TableHead key={row.year} className="text-center text-white min-w-[100px]">
                        {row.year}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">{t('gri')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-emerald-400">{formatEuro(row.gri)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">{t('capex')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-amber-400">{formatEuro(row.capex)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700 bg-slate-800/30">
                    <TableCell className="text-white font-semibold sticky left-0 bg-slate-800/30">{t('nri')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-white font-semibold">{formatEuro(row.nri)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">Main Interest</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-red-400">{formatEuro(row.main_interest)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">Main Repayment</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-amber-400">{formatEuro(row.main_repayment)}</TableCell>
                    ))}
                  </TableRow>
                  {cashflowData[0]?.sub_interest > 0 && (
                    <>
                      <TableRow className="border-slate-700">
                        <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">Sub. Interest</TableCell>
                        {cashflowData.map((row) => (
                          <TableCell key={row.year} className="text-center text-red-300">{formatEuro(row.sub_interest)}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="border-slate-700">
                        <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">Sub. Repayment</TableCell>
                        {cashflowData.map((row) => (
                          <TableCell key={row.year} className="text-center text-amber-300">{formatEuro(row.sub_repayment)}</TableCell>
                        ))}
                      </TableRow>
                    </>
                  )}
                  <TableRow className="border-slate-700 bg-slate-800/50">
                    <TableCell className="text-white font-semibold sticky left-0 bg-slate-800/50">{t('surplus')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className={`text-center font-semibold ${row.surplus >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatEuro(row.surplus)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-600">
                    <TableCell colSpan={cashflowData.length + 1} className="py-1 bg-slate-800/30"></TableCell>
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">{t('outstanding')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-white">{formatEuro(row.total_outstanding)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">{t('loanToValue')}</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-white">{formatPercent(row.ltv)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">DSCR</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-white">{row.dscr.toFixed(2)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow className="border-slate-700">
                    <TableCell className="text-slate-300 sticky left-0 bg-[#1e293b]">ICR</TableCell>
                    {cashflowData.map((row) => (
                      <TableCell key={row.year} className="text-center text-white">{row.icr.toFixed(2)}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1e293b] border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                {t('confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Dit verwijdert de aanvraag &ldquo;{selectedApplication?.application_name}&rdquo; permanent.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
